import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn, formatRelativeTime } from '../lib/utils';
import { getProductDisplayName, getProductImageUrls } from '../lib/helpers';
import { MiniProgressBar } from './ProgressBar';
import { ExternalLink, Clock, ChevronLeft, ChevronRight, UserCheck2 } from 'lucide-react';
import { PlatformBadge } from './PlatformBadge';

export function ProductCard({
  product,
  laneProgress,
  winningDetails,
  requiresApproval = false,
  hasPhaseNotification = false,
}) {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Get images from product.image_urls using proper JSONB parser
  const images = getProductImageUrls(product?.image_urls);
  const hasImages = images.length > 0;
  
  // Get display name (winning product name if available, else ecom_id)
  const displayName = getProductDisplayName(product?.ecom_id, winningDetails);
  
  const overallProgress = laneProgress ? 
    Math.round((laneProgress.research + laneProgress.ugc + laneProgress.winning + laneProgress.ads + laneProgress.shopify) / 5) : 0;

  const nextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToImage = (e, index) => {
    e.stopPropagation();
    setCurrentImageIndex(index);
  };

  return (
    <div
      data-testid={`product-card-${product?.ecom_id}`}
      onClick={() => navigate(`/product/${product?.ecom_id}`)}
      className={cn(
        'bg-white rounded-xl border border-slate-200 overflow-hidden',
        'hover:shadow-xl hover:border-cyan-300 hover:-translate-y-1',
        'transition-all duration-300 cursor-pointer group'
      )}
    >
      {/* Image Carousel Section */}
      <div className="relative h-48 bg-slate-100 overflow-hidden">
        {hasImages ? (
          <>
            <img
              src={images[currentImageIndex]}
              alt={`Product ${currentImageIndex + 1}`}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
            
            {/* Carousel Controls - only show if more than 1 image */}
            {images.length > 1 && (
              <>
                {/* Previous Button */}
                <button
                  onClick={prevImage}
                  className={cn(
                    'absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full',
                    'bg-white/90 hover:bg-white shadow-md flex items-center justify-center',
                    'opacity-0 group-hover:opacity-100 transition-opacity'
                  )}
                  data-testid="carousel-prev"
                >
                  <ChevronLeft className="w-5 h-5 text-slate-700" />
                </button>
                
                {/* Next Button */}
                <button
                  onClick={nextImage}
                  className={cn(
                    'absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full',
                    'bg-white/90 hover:bg-white shadow-md flex items-center justify-center',
                    'opacity-0 group-hover:opacity-100 transition-opacity'
                  )}
                  data-testid="carousel-next"
                >
                  <ChevronRight className="w-5 h-5 text-slate-700" />
                </button>
                
                {/* Dots Indicator */}
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {images.slice(0, 4).map((_, index) => (
                    <button
                      key={index}
                      onClick={(e) => goToImage(e, index)}
                      className={cn(
                        'w-2 h-2 rounded-full transition-all',
                        currentImageIndex === index 
                          ? 'bg-white w-4' 
                          : 'bg-white/60 hover:bg-white/80'
                      )}
                      data-testid={`carousel-dot-${index}`}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-2 rounded-full bg-slate-200 flex items-center justify-center">
                <ExternalLink className="w-8 h-8 text-slate-400" />
              </div>
              <p className="text-sm text-slate-500">No image</p>
            </div>
          </div>
        )}
        
        {/* Platform Badge */}
        <div className="absolute top-3 left-3">
          <PlatformBadge platform={product?.platform} size="sm" />
        </div>
        
        {/* Progress Badge */}
        <div className="absolute top-3 right-3 flex items-center gap-2">
          {requiresApproval && (
            <div className="bg-amber-500 text-white rounded-full p-1 shadow-md" title="Pending user approval">
              <UserCheck2 className="w-4 h-4" />
            </div>
          )}
          <div className="bg-white rounded-full px-3 py-1 text-xs font-bold text-cyan-600 shadow-md">
            {overallProgress}%
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="p-5">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-bold text-[#1E1B4B] truncate text-base font-['Manrope']">
            {displayName}
          </h3>
          {hasPhaseNotification && (
            <span
              className="ml-2 w-2 h-2 rounded-full bg-cyan-500"
              title="New update available"
            />
          )}
        </div>
        
        <div className="flex items-center gap-2 text-xs text-slate-500 mb-5">
          <Clock className="w-3 h-3" />
          <span>{formatRelativeTime(product?.created_at)}</span>
        </div>

        {/* Lane Progress Bars */}
        <div className="space-y-2.5 mb-5">
          <MiniProgressBar value={laneProgress?.research || 0} lane="research" label="Research" />
          <MiniProgressBar value={laneProgress?.ugc || 0} lane="ugc" label="UGC" />
          <MiniProgressBar value={laneProgress?.winning || 0} lane="winning" label="Winning" />
          <MiniProgressBar value={laneProgress?.ads || 0} lane="ads" label="Ads" />
          <MiniProgressBar value={laneProgress?.shopify || 0} lane="shopify" label="Shopify" />
        </div>

        {/* View Details Button */}
        <button
          data-testid={`view-details-btn-${product?.ecom_id}`}
          className={cn(
            'w-full bg-cyan-500 hover:bg-cyan-600 active:bg-cyan-700',
            'text-white font-semibold py-2.5 rounded-lg text-sm',
            'transition-all duration-200 shadow-sm hover:shadow-md',
            'btn-press'
          )}
        >
          View Details
        </button>
      </div>
    </div>
  );
}

export function ProductCardSkeleton() {
  return (
    <div className="bg-white rounded-xl border-2 border-slate-200 overflow-hidden animate-pulse">
      <div className="h-48 bg-slate-200" />
      <div className="p-5">
        <div className="h-5 bg-slate-200 rounded mb-2 w-3/4" />
        <div className="h-3 bg-slate-200 rounded mb-5 w-1/2" />
        <div className="space-y-2.5 mb-5">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="h-3 bg-slate-200 rounded w-16" />
              <div className="flex-1 h-1.5 bg-slate-200 rounded" />
              <div className="h-3 bg-slate-200 rounded w-10" />
            </div>
          ))}
        </div>
        <div className="h-10 bg-slate-200 rounded" />
      </div>
    </div>
  );
}
